import React from "react";
import { Phone, PhoneOff, PhoneCall, X, Loader2 } from "lucide-react";

/**
 * Floating call-progress indicator.
 *
 * A two-leg click-to-call is not instant and not obvious: the counsellor's own
 * phone rings first, and if nothing on screen says so they assume the button is
 * broken and click it again — which places a second real, billable call. This
 * toast exists mainly to say "answer your phone".
 */

const STATUS_COPY = {
  queued: { label: "Connecting…", hint: "Answer your phone when it rings.", tone: "pending" },
  ringing: { label: "Ringing your phone", hint: "Pick up and we'll dial the student.", tone: "pending" },
  "in-progress": { label: "Call in progress", tone: "active" },
  completed: { label: "Call completed", tone: "done" },
  "no-answer": { label: "No answer", tone: "fail" },
  busy: { label: "Line busy", tone: "fail" },
  failed: { label: "Call failed", tone: "fail" },
  canceled: { label: "Call cancelled", tone: "fail" },
  missed: { label: "Missed", tone: "fail" },
};

function formatDuration(sec) {
  if (!sec) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// No stylesheet — converted to Tailwind utilities in Phase 5.
// call-status-toast.scss was deleted in the same commit (TOKEN_CONTRACT §6.1).
//
// Its tone accents were written `var(--success, #059669)`. --success IS declared,
// but it is a bare HSL TRIPLET, so `border-left: 3px solid var(--success)` is
// invalid and the browser fell through to the hex — the toast never once used the
// token it named, in either theme. See TOKEN_CONTRACT §10's shape check.
//
// cst-in is gone in favour of the shared `slide-up`: same motion, 12px further and
// 120ms slower, which is not perceptible and is one fewer keyframe to own.

const SHELL =
  "tw-fixed tw-bottom-3 tw-left-3 tw-right-3 tw-z-toast tw-flex tw-animate-slide-up tw-items-start tw-gap-2.5 " +
  "tw-rounded-xl tw-border tw-bg-surface-raised tw-px-3.5 tw-py-3 tw-text-content tw-shadow-card-hover " +
  "motion-reduce:tw-animate-none " +
  "sm:tw-left-auto sm:tw-right-5 sm:tw-bottom-5 sm:tw-min-w-[260px] sm:tw-max-w-[min(360px,calc(100vw-2.5rem))]";

// Left border only, so the card itself stays theme-neutral in dark mode.
const TONE = {
  pending: { edge: "tw-border-l-[3px] tw-border-l-primary", icon: "tw-text-primary" },
  active: { edge: "tw-border-l-[3px] tw-border-l-success", icon: "tw-text-success" },
  done: { edge: "tw-border-l-[3px] tw-border-l-success", icon: "tw-text-success" },
  fail: { edge: "tw-border-l-[3px] tw-border-l-danger", icon: "tw-text-danger" },
};

const ICON = "tw-mt-0.5 tw-shrink-0";
const BODY = "tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[0.15rem]";
const LABEL = "tw-text-[0.8125rem] tw-font-semibold tw-leading-tight";
const HINT = "tw-text-[0.6875rem] tw-leading-snug tw-text-content-muted";
const CLOSE =
  "tw-shrink-0 tw-border-0 tw-bg-transparent tw-p-0.5 tw-leading-none tw-text-content-subtle hover:tw-text-content";

export default function CallStatusToast({ call, error, onDismiss }) {
  if (!call && !error) return null;

  if (error) {
    return (
      <div className={`${SHELL} ${TONE.fail.edge}`} role="alert">
        <PhoneOff size={16} className={`${ICON} ${TONE.fail.icon}`} />
        <div className={BODY}>
          <span className={LABEL}>Couldn't place the call</span>
          <span className={HINT}>{error}</span>
        </div>
        <button type="button" className={CLOSE} onClick={onDismiss} aria-label="Dismiss">
          <X size={14} />
        </button>
      </div>
    );
  }

  const meta = STATUS_COPY[call.status] || STATUS_COPY.queued;
  const tone = TONE[meta.tone] || TONE.pending;
  const pending = meta.tone === "pending";
  const duration = formatDuration(call.talkTimeSec);

  // In browser mode there is no handset to pick up, and "answer your phone" would
  // send the counsellor to the wrong device while the call rings in the tab they
  // are already looking at.
  const label = call.callMode === "webrtc" && call.status === "ringing"
    ? "Ringing in your browser"
    : meta.label;
  const hint = call.callMode === "webrtc" && pending
    ? "Answer in the softphone and we'll dial the student."
    : meta.hint;

  const Icon = meta.tone === "active" ? PhoneCall
    : meta.tone === "fail" ? PhoneOff
    : Phone;

  return (
    <div className={`${SHELL} ${tone.edge}`} role="status" aria-live="polite">
      {pending
        ? <Loader2 size={16} className={`${ICON} ${tone.icon} tw-animate-spin motion-reduce:[animation-duration:2.5s]`} />
        : <Icon size={16} className={`${ICON} ${tone.icon}`} />}
      <div className={BODY}>
        <span className={LABEL}>{label}</span>
        {duration && <span className={HINT}>Talked for {duration}</span>}
        {!duration && hint && <span className={HINT}>{hint}</span>}
      </div>
      <button type="button" className={CLOSE} onClick={onDismiss} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
}
